import { useRef, useState } from "react";
import api, { runAiJob, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { PageHeader } from "../components/ui-helpers";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "../components/ui/alert-dialog";
import { FileArrowUp, Sparkle, FilePdf, FileDoc, LockKey, X, ListBullets } from "@phosphor-icons/react";

const ACCEPT = ".pdf,.docx,.xlsx,.xlsm,.csv,.tsv,.txt,.md,.log,.json,.xml,.yaml,.yml,.ini,.cfg,.rpt,.out,.dat";

const SECTIONS = [
  { key: "summary", title: "Executive Summary", cls: "border-slate-300 bg-slate-50" },
  { key: "answer", title: "Answer to Your Question", cls: "border-violet-300 bg-violet-50" },
  { key: "key_findings", title: "Key Findings", cls: "border-blue-300 bg-blue-50" },
  { key: "data_quality", title: "Data Quality Issues", cls: "border-orange-300 bg-orange-50" },
  { key: "trends", title: "Trends & Patterns", cls: "border-emerald-300 bg-emerald-50" },
  { key: "compliance", title: "NERC Compliance Observations", cls: "border-amber-300 bg-amber-50" },
];

const Section = ({ title, value, cls }) => {
  if (!value || (Array.isArray(value) && value.length === 0)) return null;
  const tid = `da-section-${title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`;
  return (
    <div className={`border ${cls}`} data-testid={tid}>
      <p className="text-xs font-semibold uppercase tracking-wider px-4 py-2 border-b border-black/5">{title}</p>
      <div className="px-4 py-3 text-sm text-slate-800">
        {Array.isArray(value) ? (
          <ul className="space-y-1.5">
            {value.map((x, i) => (
              <li key={i} className="flex gap-2">
                <ListBullets size={15} className="mt-0.5 shrink-0 text-slate-400" />
                <span>{x}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="whitespace-pre-wrap leading-relaxed">{value}</p>
        )}
      </div>
    </div>
  );
};

export default function DataAnalyzer() {
  const ref = useRef(null);
  const [file, setFile] = useState(null);
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [confirm, setConfirm] = useState(null); // { pages, credits, reason, over_limit }

  const run = async (approvedCredits) => {
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (question.trim()) fd.append("question", question.trim());
      fd.append("approved_credits", String(approvedCredits || 0));
      const data = await runAiJob("/data-analyzer/analyze", fd,
        { headers: { "Content-Type": "multipart/form-data" } }, { maxTries: 450 });
      if (data?.needs_confirmation) { setConfirm(data); return; }
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Analysis failed. Please try again.");
    } finally { setBusy(false); }
  };

  const analyze = () => {
    if (!file) { toast.error("Choose a file to analyze first."); return; }
    run(0);
  };

  const approveAndRun = () => {
    const c = confirm?.credits || 0;
    setConfirm(null);
    run(c);
  };

  const reset = () => { setFile(null); setQuestion(""); setResult(null); setConfirm(null); };

  const download = async (fmt) => {
    try {
      const resp = await api.post(`/data-analyzer/export?fmt=${fmt}`, result, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `data-analysis.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error("Export failed. Please try again.");
    }
  };

  return (
    <div className="p-6 space-y-5 max-w-6xl" data-testid="data-analyzer-page">
      <PageHeader title="Data Analyzer"
                  subtitle="Upload a spreadsheet, report or document and get an AI analysis — summary, key findings, data-quality issues, trends and NERC-relevant observations." />

      <div className="flex items-center gap-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 px-3 py-2" data-testid="da-privacy-note">
        <LockKey size={14} weight="fill" className="shrink-0" />
        Privacy-first: your file is analyzed in memory and is <b>never stored</b> in ClaroShield. Its contents are sent to the AI model only to produce this analysis (standard API calls are not used to train models), and the report you download is generated on the fly — nothing is retained.
      </div>

      <div className="border border-border bg-card p-5 space-y-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Data file</p>
          <p className="text-xs text-muted-foreground mt-0.5">PDF, Word (.docx), Excel (.xlsx), CSV or text · up to 50 MB · up to ~2,250 pages · 1 credit per 750 pages (you approve the cost for large files)</p>
          <input ref={ref} type="file" accept={ACCEPT} className="hidden" data-testid="da-file-input"
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) setFile(f); e.target.value = ""; }} />
          {file ? (
            <div className="mt-3 flex items-center justify-between gap-2 border border-border bg-secondary/40 px-3 py-2 max-w-md">
              <span className="text-sm truncate" data-testid="da-file-name">{file.name}</span>
              <button onClick={() => setFile(null)} className="text-muted-foreground hover:text-foreground" data-testid="da-file-clear">
                <X size={14} />
              </button>
            </div>
          ) : (
            <Button variant="outline" className="mt-3 rounded-none max-w-md w-full" onClick={() => ref.current?.click()} data-testid="da-file-btn">
              <FileArrowUp size={16} className="mr-1.5" /> Choose file
            </Button>
          )}
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Ask a question (optional)</p>
          <p className="text-xs text-muted-foreground mt-0.5">e.g. "Flag any assets missing a CIP categorization" or "Which rows are past their due date?"</p>
          <Textarea value={question} onChange={(e) => setQuestion(e.target.value)} rows={2}
                    className="mt-2 rounded-none" placeholder="Leave blank for a full general analysis…"
                    data-testid="da-question-input" />
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <Button onClick={analyze} disabled={busy || !file} className="rounded-none bg-primary hover:bg-primary/90" data-testid="da-analyze-btn">
            <Sparkle size={16} weight="fill" className="mr-1.5" /> {busy ? "Analyzing…" : "Analyze data"}
          </Button>
          {(result || file) && (
            <Button variant="outline" className="rounded-none" onClick={reset} disabled={busy} data-testid="da-reset-btn">Start over</Button>
          )}
          <span className="text-xs text-muted-foreground">1 credit per 750 pages · you'll approve the cost for large files</span>
        </div>
      </div>

      {busy && (
        <div className="text-sm text-muted-foreground border border-border bg-card p-4" data-testid="da-loading">
          Reading your file and running the analysis… this can take up to a minute for large files.
        </div>
      )}

      {result && (
        <div className="space-y-4" data-testid="da-results">
          <div className="flex items-end justify-between flex-wrap gap-3">
            <p className="text-xs text-muted-foreground">
              <span className="font-mono">{result.filename}</span>
              {result.source && <> · {result.source}</>}
              {result.pages ? <> · {result.pages} pages</> : null}
              {result.segments > 1 ? <> · analyzed in {result.segments} segments</> : null}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" className="rounded-none" onClick={() => download("pdf")} data-testid="da-export-pdf">
                <FilePdf size={16} className="mr-1.5" /> PDF
              </Button>
              <Button variant="outline" className="rounded-none" onClick={() => download("docx")} data-testid="da-export-docx">
                <FileDoc size={16} className="mr-1.5" /> Word
              </Button>
            </div>
          </div>
          {result.coverage_note && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2" data-testid="da-coverage-note">
              {result.coverage_note}
            </div>
          )}
          {SECTIONS.map((s) => <Section key={s.key} title={s.title} value={result[s.key]} cls={s.cls} />)}
        </div>
      )}

      <AlertDialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <AlertDialogContent data-testid="da-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm AI credit cost</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.reason}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="text-sm border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900" data-testid="da-confirm-cost">
            This analysis will use <b>{confirm?.credits} AI credits</b>.
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="da-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={approveAndRun} data-testid="da-confirm-approve">
              Approve &amp; analyze ({confirm?.credits} credits)
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
